// src/core/theme/theme.ts
import { createTheme } from "@mui/material/styles";
import type { ThemeMode } from "@core/context/theme-context";
import customBreakpoints from "./breakpoints";
import { ResponsivePadding } from "@client/config/const";

const getTheme = (mode: ThemeMode) => {
  let palette = {
    mode: "light" as const,
    primary: {
      main: "#f6f6fe",
      contrastText: "#1c252e",
      dark: "#d6d6f6",
    },
    secondary: {
      main: "#dc004e",
      contrastText: "#000",
    },
    error: {
      main: "#d32f2f",
    },
    warning: {
      main: "#ed6c02",
    },
    info: {
      main: "#0288d1",
    },
    success: {
      main: "#2e7d32",
    },
    background: {
      default: "#ffffff",
      paper: "#f5f5f5",
    },
  };

  return createTheme({
    palette,
    breakpoints: customBreakpoints,
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      button: {
        textTransform: "none",
      },
      subtitle1: {
        color: "#696969",
      },
      subtitle2: {
        color: "#0b8be8",
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.5,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: "h2",
            h2: "h2",
            h3: "h2",
            h4: "h2",
            h5: "h2",
            h6: "h2",
            subtitle1: "h2",
            subtitle2: "h2",
            body1: "span",
            body2: "span",
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: ({ palette, breakpoints }) => ({
          body: {
            margin: 0,
            backgroundColor: palette.background.default,
            color: palette.text?.primary || palette.primary.contrastText,
            transition: "background-color 0.3s ease, color 0.3s ease",
          },

          header: {
            flexShrink: 0, // Prevent header from being compressed
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            width: "100%",
            margin: "0 auto",
            [breakpoints.up("xs")]: {},
            [breakpoints.up("sm")]: {},
            [breakpoints.up("md")]: {},
            [breakpoints.up("lg")]: {},
            [breakpoints.up("xl")]: {},
          },

          main: {
            flex: 1, // Key attribute: occupy the remaining space
            boxSizing: "border-box",
            width: "100%",
            margin: "0 auto",
            // Responsive padding
            [breakpoints.up("xs")]: {},
            [breakpoints.up("sm")]: {},
            [breakpoints.up("md")]: {},
            [breakpoints.up("lg")]: {},
            [breakpoints.up("xl")]: {},
          },

          footer: {
            flexShrink: 0, // Prevent footer from being compressed
            backgroundColor: palette.background.paper,
            width: "100%",
            margin: "0 auto",
            // Responsive padding
            [breakpoints.up("xs")]: {},
            [breakpoints.up("sm")]: {},
            [breakpoints.up("md")]: {},
            [breakpoints.up("lg")]: {},
            [breakpoints.up("xl")]: {},
          },
          a: {
            color: "#0B8BE8",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "none",
            },
          },
          p: {
            margin: 0,
            padding: 0,
          },
        }),
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
          containedPrimary: {
            backgroundColor: palette.primary.main,
            "&:hover": {
              backgroundColor: palette.primary.dark || palette.primary.main,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: palette.primary.main,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            padding: "0 !important",
            // border: process.env.NODE_ENV === "development" ? "1px solid red" : "none",
          },
          dense: {
            minHeight: "48px",
          },
          regular: {
            minHeight: 56,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.background.paper,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          },
        },
      },
    },
  });
};

export default getTheme;
